import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, Outlet, Route, BrowserRouter, Routes, useParams } from 'react-router-dom';
import { setApiConfig, useSpace } from '@pilot/api';
import { Providers } from '@/components/providers';
import { SpaceLayoutClient } from '@/components/layouts/space-layout-client';
import './app/globals.css';

const AuthLayout = React.lazy(() => import('@/app/auth/layout'));
const SignInPage = React.lazy(() => import('@/app/auth/sign-in/page'));
const SignUpPage = React.lazy(() => import('@/app/auth/sign-up/page'));
const SpacesPage = React.lazy(() => import('@/app/spaces/page'));
const CreateSpacePage = React.lazy(() => import('@/app/spaces/create/page'));
const SettingsPage = React.lazy(() => import('@/app/settings/page'));
const SpaceOverviewPage = React.lazy(() => import('@/app/spaces/[slug]/overview/page'));
const BoardPage = React.lazy(() => import('@/app/spaces/[slug]/board/page'));
const CyclesPage = React.lazy(() => import('@/app/spaces/[slug]/cycles/page'));
const IssuesPage = React.lazy(() => import('@/app/spaces/[slug]/issues/page'));
const ModulesPage = React.lazy(() => import('@/app/spaces/[slug]/modules/page'));
const TimelinePage = React.lazy(() => import('@/app/spaces/[slug]/timeline/page'));
const TeamsPage = React.lazy(() => import('@/app/spaces/[slug]/teams/page'));
const WorkItemsPage = React.lazy(() => import('@/app/spaces/[slug]/work-items/page'));
const MailPage = React.lazy(() => import('@/app/spaces/[slug]/mail/page'));
const MeetingsPage = React.lazy(() => import('@/app/spaces/[slug]/meetings/page'));
const ChatPage = React.lazy(() => import('@/app/spaces/[slug]/chat/page'));
const ChatLayout = React.lazy(() => import('@/app/spaces/[slug]/chat/layout'));
const FeedPage = React.lazy(() => import('@/app/spaces/[slug]/feed/page'));
const FeedChannelPage = React.lazy(() => import('@/components/feed/channel-view').then(module => ({ default: module.ChannelView })));
const FeedLayout = React.lazy(() => import('@/app/spaces/[slug]/feed/layout'));
const WikiPage = React.lazy(() => import('@/app/spaces/[slug]/wiki/page'));
const WikiDetailPage = React.lazy(() => import('@/app/spaces/[slug]/wiki/[pageId]/page'));
const WikiLayout = React.lazy(() => import('@/app/spaces/[slug]/wiki/layout'));
const SpaceSettingsPage = React.lazy(() => import('@/app/spaces/[slug]/settings/page'));
const MembersSettingsPage = React.lazy(() => import('@/app/spaces/[slug]/settings/members/page'));
const IntegrationsSettingsPage = React.lazy(() => import('@/app/spaces/[slug]/settings/integrations/page'));
const ProjectOverviewPage = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/overview/page'));
const ProjectBoardPage = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/board/page'));
const ProjectIssuesPage = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/issues/page'));
const ProjectTeamsPage = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/teams/page'));
const ProjectTimelinePage = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/timeline/page'));
const ProjectPagesPage = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/pages/page'));
const ProjectPageDetail = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/pages/[pageId]/page'));
const ProjectPagesLayout = React.lazy(() => import('@/app/spaces/[slug]/projects/[projectCode]/pages/layout'));

const apiUrl = import.meta.env.VITE_API_URL ?? '';
setApiConfig({ baseUrl: apiUrl });

const config = {
  apiUrl,
  landingUrl: import.meta.env.VITE_LANDING_URL ?? 'http://localhost:3000',
  oauth: {
    google: { clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '' },
    apple: { clientId: import.meta.env.VITE_APPLE_CLIENT_ID ?? '' },
  },
};

function SpaceRoute() {
  const { slug = '' } = useParams();
  const { data: space, isLoading, error } = useSpace(slug);
  if (isLoading) return <div className="grid min-h-screen place-items-center"><div className="size-10 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  if (error || !space) return <Navigate to="/spaces?error=not-a-member" replace />;
  return (
    <SpaceLayoutClient
      space={{ id: (space as any).id, name: space.name, slug: space.slug, description: space.description ?? undefined, icon: space.icon ?? undefined }}
      membership={{ id: `${(space as any).id}:membership`, role: space.role as any, joinedAt: (space as any).createdAt ?? new Date().toISOString() }}
    >
      <Outlet />
    </SpaceLayoutClient>
  );
}

const WithOutlet = ({ component: Component }: { component: React.ComponentType<{ children: React.ReactNode }> }) => <Component><Outlet /></Component>;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/spaces" replace />} />
      <Route element={<WithOutlet component={AuthLayout} />}>
        <Route path="/auth/sign-in" element={<SignInPage />} />
        <Route path="/auth/sign-up" element={<SignUpPage />} />
      </Route>
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/spaces" element={<SpacesPage />} />
      <Route path="/spaces/create" element={<CreateSpacePage />} />
      <Route path="/spaces/:slug" element={<SpaceRoute />}>
        <Route index element={<SpaceOverviewPage />} />
        <Route path="overview" element={<SpaceOverviewPage />} />
        <Route path="board" element={<BoardPage />} />
        <Route path="cycles" element={<CyclesPage />} />
        <Route path="issues" element={<IssuesPage />} />
        <Route path="modules" element={<ModulesPage />} />
        <Route path="timeline" element={<TimelinePage />} />
        <Route path="teams" element={<TeamsPage />} />
        <Route path="work-items" element={<WorkItemsPage />} />
        <Route path="mail" element={<MailPage />} />
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="chat" element={<WithOutlet component={ChatLayout} />}><Route index element={<ChatPage />} /></Route>
        <Route path="feed" element={<WithOutlet component={FeedLayout} />}><Route index element={<FeedPage />} /><Route path=":channelSlug" element={<FeedChannelPage />} /></Route>
        <Route path="wiki" element={<WithOutlet component={WikiLayout} />}><Route index element={<WikiPage />} /><Route path=":pageId" element={<WikiDetailPage />} /></Route>
        <Route path="settings" element={<Outlet />}>
          <Route index element={<SpaceSettingsPage />} />
          <Route path="members" element={<MembersSettingsPage />} />
          <Route path="integrations" element={<IntegrationsSettingsPage />} />
          <Route path="email" element={<IntegrationsSettingsPage />} />
          <Route path="storage" element={<IntegrationsSettingsPage />} />
          <Route path="meetings" element={<IntegrationsSettingsPage />} />
          <Route path="realtime" element={<Navigate to="../integrations" replace />} />
        </Route>
        <Route path="integrations" element={<Navigate to="settings/integrations" replace />} />
        <Route path="projects/:projectCode">
          <Route index element={<ProjectOverviewPage />} />
          <Route path="overview" element={<ProjectOverviewPage />} />
          <Route path="board" element={<ProjectBoardPage />} />
          <Route path="issues" element={<ProjectIssuesPage />} />
          <Route path="teams" element={<ProjectTeamsPage />} />
          <Route path="timeline" element={<ProjectTimelinePage />} />
          <Route path="integrations" element={<IntegrationsSettingsPage />} />
          <Route path="pages" element={<WithOutlet component={ProjectPagesLayout} />}><Route index element={<ProjectPagesPage />} /><Route path=":pageId" element={<ProjectPageDetail />} /></Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/spaces" replace />} />
    </Routes>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Providers config={config}>
        <React.Suspense fallback={<div className="grid min-h-screen place-items-center"><div className="size-10 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
          <AppRoutes />
        </React.Suspense>
      </Providers>
    </BrowserRouter>
  </React.StrictMode>,
);
